import {
  AddressBookIcon,
  BrainIcon,
  LightbulbIcon,
  ListChecksIcon,
  PencilLineIcon,
  SlidersHorizontalIcon,
  ArrowsClockwiseIcon,
  BugIcon,
  ChatTextIcon,
  CubeIcon,
  DatabaseIcon,
  EnvelopeSimpleIcon,
  FilePdfIcon,
  FileTextIcon,
  FunnelIcon,
  GearIcon,
  Icon,
  KeyIcon,
  ListBulletsIcon,
  LockKeyIcon,
  NoteIcon,
  PaletteIcon,
  PaperPlaneTiltIcon,
  PhoneIcon,
  PlugsIcon,
  PlugsConnectedIcon,
  PulseIcon,
  RobotIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  SidebarIcon,
  TableIcon,
  TimerIcon,
  UsersIcon,
  UsersThreeIcon,
  GraphIcon,
} from '@phosphor-icons/react';
import { msg } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { FeatureFlagKey } from '@erp/shared-types';

export type OrganizationSettingsSectionId =
  | 'address-book'
  | 'agent-pages'
  | 'agent-resources'
  | 'api-keys'
  | 'approvals'
  | 'azure-email-registration'
  | 'gmail-email-registration'
  | 'brand-kit'
  | 'control-plane'
  | 'credentials'
  | 'custom-fields'
  | 'data-mappings'
  | 'default-recipients'
  | 'edi'
  | 'edi-guidelines'
  | 'email-templates'
  | 'enums'
  | 'erp'
  | 'external-apis'
  | 'famous-health'
  | 'general'
  | 'harness-brain'
  | 'harness-corrections'
  | 'harness-memories'
  | 'harness-proposals'
  | 'harness-tool-calls'
  | 'harness-tool-policies'
  | 'knowledge-corrections'
  | 'knowledge-memories'
  | 'knowledge-proposals'
  | 'guardrail-policies'
  | 'known-domains'
  | 'data-sources'
  | 'data-source-loads'
  | 'mcp-server'
  | 'members'
  | 'memory-bank-notes'
  | 'modules'
  | 'otp-secrets'
  | 'page-sections'
  | 'pdf-templates'
  | 'rabbitmq-health'
  | 'report-palettes'
  | 'retrigger-ap-invoice-draft'
  | 'resource-privileges'
  | 'sales-order-review-ui'
  | 'sandbox-config'
  | 'schema-definitions'
  | 'sentry-test'
  | 'sync-relay'
  | 'task-pages'
  | 'task-types'
  | 'teams'
  | 'template-blocks'
  | 'test-forward-original-graph-message'
  | 'test-timeout'
  | 'test-upn-write-access'
  | 'twilio'
  | 'voice-agents';

export type OrganizationSettingsGroupId =
  | 'general'
  | 'team'
  | 'data-model'
  | 'design'
  | 'communication'
  | 'security'
  | 'ai'
  | 'agent-harness'
  | 'knowledge'
  | 'integrations'
  | 'developer';

export type OrganizationSettingsSection = {
  id: OrganizationSettingsSectionId;
  title: string;
  subtitle?: string;
  icon: Icon;
  group: OrganizationSettingsGroupId;
  gentlyOnly?: boolean;
  requiredModule?: ProductModule;
  /**
   * Hide this section unless the viewer holds the listed permission. Gently
   * staff bypass this via the `gently` flag in the filter — only set this for
   * sections whose page would otherwise 403 for users without the permission.
   */
  requiredPermission?: { action: Action; subject: Subject };
  /**
   * Like `requiredPermission`, but satisfied by any one of the listed
   * permissions. For sections that administer something shared across several
   * page kinds (e.g. page sections hold both task pages and agent pages).
   */
  requiredAnyPermission?: ReadonlyArray<{ action: Action; subject: Subject }>;
  featureFlag?: FeatureFlagKey;
  /**
   * Hide this section while this flag is on. Lets a section stand in for
   * another one that shows the same page behind a different flag, so a viewer
   * with both flags sees it once.
   */
  hiddenWhenFeatureFlag?: FeatureFlagKey;
  /**
   * Also list the section in the main sidebar as a shortcut straight into
   * Organization Settings. The entry inherits the section's gates —
   * `gentlyOnly` → admin-only, `featureFlag`, `requiredModule`,
   * `requiredPermission` / `requiredAnyPermission` → any-of permissions — so
   * it never shows to someone the section itself would turn away.
   * (`requiresFamousIntegration` has no sidebar equivalent; don't combine.)
   * `label` is a `msg()`-encoded string, `group` a sidebar section title.
   */
  sidebarNav?: { group: string; label: string; order?: number };
  /**
   * Hide this section unless Famous is integrated for the organization
   * (Famous module enabled or the Famous integration connected) — the same
   * gate the backend's isFamousIntegrationEnabledForOrg applies.
   */
  requiresFamousIntegration?: boolean;
};

export type OrganizationSettingsGroup = {
  id: OrganizationSettingsGroupId;
  title: string;
};

// General stays on top; the rest are alphabetical by title.
export const ORGANIZATION_SETTINGS_GROUPS: OrganizationSettingsGroup[] = [
  { id: 'general', title: 'General' },
  { id: 'team', title: 'Accounts' },
  { id: 'ai', title: 'AI' },
  { id: 'agent-harness', title: 'Agent Harness' },
  { id: 'communication', title: 'Communication' },
  { id: 'data-model', title: 'Data Model' },
  { id: 'design', title: 'Design & Pages' },
  { id: 'developer', title: 'Developer Tools' },
  { id: 'integrations', title: 'Integrations' },
  { id: 'knowledge', title: 'Knowledge' },
  { id: 'security', title: 'Security' },
];

export const ORGANIZATION_SETTINGS_SECTIONS: OrganizationSettingsSection[] = [
  {
    id: 'address-book',
    title: 'Address Book',
    subtitle: 'Manage your organization’s contacts and saved email addresses',
    icon: AddressBookIcon,
    group: 'communication',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.Contact,
    },
  },
  {
    id: 'agent-pages',
    title: 'Agent Pages',
    subtitle:
      'Mount a background agent in the sidebar as its own page, with per-page audiences',
    icon: RobotIcon,
    group: 'ai',
    requiredPermission: {
      action: Action.Update,
      subject: Subject.BackgroundAgent,
    },
  },
  {
    id: 'agent-resources',
    title: 'Agent Resources',
    subtitle:
      'Manage concurrency-gated resources that agents can exclusively hold',
    icon: DatabaseIcon,
    group: 'ai',
    gentlyOnly: true,
  },
  {
    id: 'api-keys',
    title: 'API Keys',
    subtitle: 'Manage API keys for Data Sync Agents',
    icon: KeyIcon,
    group: 'team',
    requiredPermission: { action: Action.Read, subject: Subject.ApiKey },
  },
  {
    id: 'approvals',
    title: 'Approvals',
    subtitle: 'Configure discount and order approval levels and rules',
    icon: ShieldCheckIcon,
    group: 'data-model',
    // Gated to Gently staff until the dedicated Subject.ApprovalConfig RBAC
    // wiring lands on the backend. Approval thresholds are sensitive
    // configuration — better to hide the surface than expose it ungated.
    gentlyOnly: true,
  },
  {
    id: 'azure-email-registration',
    title: 'Azure Email Registration',
    subtitle: 'Configure email registration from your Azure tenant',
    icon: EnvelopeSimpleIcon,
    group: 'communication',
    gentlyOnly: true,
  },
  {
    id: 'gmail-email-registration',
    title: 'Gmail Email Registration',
    subtitle: 'Connect Google Workspace mailboxes receiving group emails',
    icon: EnvelopeSimpleIcon,
    group: 'communication',
    gentlyOnly: true,
  },
  {
    id: 'brand-kit',
    title: 'Brand Kit',
    subtitle: 'Your brand colors, typography, logos, and voice for emails',
    icon: PaletteIcon,
    group: 'communication',
  },
  {
    id: 'sandbox-config',
    title: 'Code Sandbox',
    subtitle:
      'Configure resource limits, network access, and environment for code sandboxes',
    icon: ShieldCheckIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'control-plane',
    title: 'Control Plane',
    subtitle: 'Connection status and management',
    icon: CubeIcon,
    group: 'integrations',
    gentlyOnly: true,
  },
  {
    id: 'credentials',
    title: 'Credentials',
    subtitle: 'Manage passwords, tokens, and other secrets',
    icon: LockKeyIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'custom-fields',
    title: 'Custom Fields',
    subtitle: 'Manage your core custom fields',
    icon: FunnelIcon,
    group: 'data-model',
  },
  {
    id: 'data-mappings',
    title: 'Data Mappings',
    subtitle:
      'Manage lookup tables business logic resolves by slug, plus the legacy module-config maps',
    icon: TableIcon,
    group: 'data-model',
  },
  {
    id: 'data-sources',
    title: 'Data Sources',
    subtitle: 'Configure and monitor AS/400 and vendor price list data sources',
    icon: DatabaseIcon,
    group: 'data-model',
    gentlyOnly: true,
    featureFlag: 'data-sources-enabled',
  },
  {
    id: 'data-source-loads',
    title: 'Data Source Loads',
    subtitle:
      'Direct loads running in this organization: the batch, every load, and cancel',
    icon: DatabaseIcon,
    group: 'data-model',
    gentlyOnly: true,
    featureFlag: 'data-sources-enabled',
  },
  {
    id: 'default-recipients',
    title: 'Default Recipients',
    subtitle:
      'Default sending mailbox and auto-Cc recipients for general, discount, purchase-order, and sales-order email',
    icon: PaperPlaneTiltIcon,
    group: 'communication',
    requiredPermission: {
      action: Action.Update,
      subject: Subject.OrganizationConfig,
    },
  },
  {
    id: 'edi-guidelines',
    title: 'EDI',
    subtitle:
      'Vendor EDI implementation guidelines — the documents stating what each trading partner will actually transmit',
    icon: FileTextIcon,
    group: 'integrations',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.EdiGuideline,
    },
    featureFlag: 'edi-guidelines-enabled',
  },
  {
    id: 'edi',
    // "EDI (Orderful)" rather than "EDI": this section is outbound customer
    // configuration for one transport, and the guidelines section above is
    // inbound vendor documents. Two entries both reading "EDI" would be a
    // coin flip for whoever is looking for one of them.
    title: 'EDI (Orderful)',
    subtitle: 'Orderful trading-partner and transaction settings',
    icon: ShoppingBagIcon,
    group: 'integrations',
    requiredModule: ProductModule.ORDERFUL_EDI,
  },
  {
    id: 'email-templates',
    title: 'Email Templates',
    subtitle: 'Manage reusable email templates with dynamic variables',
    icon: EnvelopeSimpleIcon,
    group: 'communication',
  },
  {
    id: 'enums',
    title: 'Enums',
    subtitle: 'Manage your core enums',
    icon: CubeIcon,
    group: 'data-model',
  },
  {
    id: 'erp',
    title: 'ERP Submission API',
    subtitle:
      'ERP submission APIs the sales-order Submission step can target (Famous, Oracle Fusion, Oracle EBS)',
    icon: DatabaseIcon,
    group: 'integrations',
    requiredModule: ProductModule.ORDER_MANAGEMENT,
  },
  {
    id: 'external-apis',
    title: 'External APIs',
    subtitle:
      'Register external HTTP APIs for workflow calls and ERP submissions',
    icon: PlugsConnectedIcon,
    group: 'integrations',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.Integration,
    },
  },
  {
    id: 'famous-health',
    title: 'Famous Health',
    subtitle: 'Famous Oracle connectivity — run an on-demand connection check',
    icon: PulseIcon,
    group: 'developer',
    gentlyOnly: true,
    requiresFamousIntegration: true,
  },
  {
    id: 'guardrail-policies',
    title: 'Guardrail Policies',
    subtitle: 'Create and manage AI safety policies for workflow steps',
    icon: ShieldCheckIcon,
    group: 'ai',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.GuardrailPolicy,
    },
  },
  {
    id: 'known-domains',
    title: 'Known Domains',
    subtitle: 'Configure internal/SSO domains for user authentication',
    icon: GearIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'members',
    title: 'Members',
    subtitle: 'Manage members of your organization',
    icon: UsersIcon,
    group: 'team',
    requiredPermission: { action: Action.Read, subject: Subject.User },
  },
  {
    id: 'harness-brain',
    title: 'Knowledge Graph',
    subtitle:
      'What the agent has learned about your orders and how it all connects — stored memories, proactive suggestions and the learning signals behind them',
    icon: GraphIcon,
    group: 'agent-harness',
    requiredPermission: { action: Action.Read, subject: Subject.MemoryRule },
    featureFlag: 'agent-harness-enabled',
    sidebarNav: {
      group: 'Admin',
      label: msg('Knowledge Graph', {
        context: 'sidebar nav label, Admin section',
      }),
      order: 70,
    },
  },
  {
    id: 'harness-corrections',
    title: 'Learning Signals',
    subtitle:
      'Every human correction on a sales-order draft, the raw signal the learning loop mines',
    icon: PencilLineIcon,
    group: 'agent-harness',
    gentlyOnly: true,
    featureFlag: 'agent-harness-enabled',
    hiddenWhenFeatureFlag: 'memory-learning-enabled',
  },
  {
    id: 'harness-proposals',
    title: 'Proactive Suggestions',
    subtitle:
      'Review what the agent learned from recurring corrections before it becomes a stored memory',
    icon: LightbulbIcon,
    group: 'agent-harness',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.MemoryProposal,
    },
    featureFlag: 'agent-harness-enabled',
    hiddenWhenFeatureFlag: 'memory-learning-enabled',
  },
  {
    id: 'harness-memories',
    title: 'Stored Memories',
    subtitle:
      'Approved rules the agent applies deterministically, the free-text notes it reads, and a place to teach it directly',
    icon: BrainIcon,
    group: 'agent-harness',
    requiredPermission: { action: Action.Read, subject: Subject.MemoryRule },
    featureFlag: 'agent-harness-enabled',
    hiddenWhenFeatureFlag: 'memory-learning-enabled',
  },
  {
    id: 'harness-tool-calls',
    title: 'Tool Calls',
    subtitle:
      'Every action the AI assistant took or looked up, by page, tool, person and outcome',
    icon: ListChecksIcon,
    group: 'agent-harness',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.HarnessToolCall,
    },
    featureFlag: 'agent-harness-enabled',
  },
  {
    id: 'harness-tool-policies',
    title: 'Tool Policies',
    subtitle:
      'Which assistant actions this organization allows, which must be previewed first, and how many changes one message may make',
    icon: SlidersHorizontalIcon,
    group: 'agent-harness',
    requiredPermission: {
      action: Action.Update,
      subject: Subject.OrganizationConfig,
    },
    featureFlag: 'agent-harness-enabled',
  },
  // Knowledge holds the learning review pages above behind their own flag.
  // With it on, these replace the Agent Harness copies; the Knowledge Graph
  // stays an Agent Harness page. With it off, Agent Harness is unchanged.
  // Same order as Agent Harness: signals, then suggestions, then memories.
  {
    id: 'knowledge-corrections',
    title: 'Learning Signals',
    subtitle: 'Every human correction the learning loop reads',
    icon: PencilLineIcon,
    group: 'knowledge',
    gentlyOnly: true,
    featureFlag: 'memory-learning-enabled',
  },
  {
    id: 'knowledge-proposals',
    title: 'Proactive Suggestions',
    subtitle:
      'Review what was learned from repeated corrections before it is applied',
    icon: LightbulbIcon,
    group: 'knowledge',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.MemoryProposal,
    },
    featureFlag: 'memory-learning-enabled',
  },
  {
    id: 'knowledge-memories',
    title: 'Stored Memories',
    subtitle: 'Approved rules applied automatically, and free-text notes',
    icon: BrainIcon,
    group: 'knowledge',
    requiredPermission: { action: Action.Read, subject: Subject.MemoryRule },
    featureFlag: 'memory-learning-enabled',
  },
  {
    id: 'mcp-server',
    title: 'MCP server',
    subtitle:
      'Choose which tools external agents can use when they connect to your organization over MCP',
    icon: PlugsIcon,
    group: 'integrations',
    requiredPermission: {
      action: Action.Update,
      subject: Subject.McpServer,
    },
  },
  {
    id: 'memory-bank-notes',
    title: 'Memory Bank Notes',
    subtitle: 'Manage and search through your AI memory bank notes',
    icon: NoteIcon,
    group: 'ai',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.MemoryBankNote,
    },
  },
  {
    id: 'modules',
    title: 'Modules',
    subtitle: 'Setup and manage your modules',
    icon: KeyIcon,
    group: 'integrations',
    gentlyOnly: true,
  },
  {
    id: 'general',
    title: 'Organization',
    subtitle: 'Core organization settings',
    icon: GearIcon,
    group: 'general',
    gentlyOnly: true,
  },
  {
    id: 'otp-secrets',
    title: 'OTP Secrets',
    subtitle: 'Manage TOTP secrets for automated MFA',
    icon: LockKeyIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'page-sections',
    title: 'Page Sections',
    subtitle:
      'Custom sidebar sections that group task pages, agent pages, and other org-created pages',
    icon: SidebarIcon,
    group: 'design',
    // Mirrors the server's PAGE_SECTION_ADMIN_CHECKS: any admin action on any
    // page kind, so a Create-only or Delete-only page admin still finds the card.
    requiredAnyPermission: [Subject.TaskPage, Subject.BackgroundAgent].flatMap(
      (subject) => [
        { action: Action.Create, subject },
        { action: Action.Update, subject },
        { action: Action.Delete, subject },
      ],
    ),
  },
  {
    id: 'pdf-templates',
    title: 'PDF Templates',
    subtitle: 'Manage reusable PDF document templates with dynamic variables',
    icon: FilePdfIcon,
    group: 'communication',
  },
  {
    id: 'rabbitmq-health',
    title: 'RabbitMQ Health',
    subtitle: 'Broker connectivity, connection state, and channel usage',
    icon: PulseIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'retrigger-ap-invoice-draft',
    title: 'Retrigger AP Invoice Draft',
    subtitle:
      'Re-publish AP_INVOICE_DRAFT_CREATED for a newline-separated list of invoice ids',
    icon: ArrowsClockwiseIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'report-palettes',
    title: 'Report Palettes',
    subtitle:
      'The colors Reports draw with: a default palette, and others a Report can pick',
    icon: PaletteIcon,
    group: 'design',
    // Palettes are organization settings: the same permission as the others.
    requiredPermission: {
      action: Action.Update,
      subject: Subject.OrganizationConfig,
    },
  },
  {
    id: 'resource-privileges',
    title: 'Resource Privileges',
    subtitle:
      'Tenant-level data scoping — control which records each principal can see',
    icon: ShieldCheckIcon,
    group: 'security',
    gentlyOnly: true,
  },
  {
    id: 'sales-order-review-ui',
    title: 'Sales Orders Page Design',
    subtitle: 'Metrics, columns, fields, layout & shipping options',
    icon: PaletteIcon,
    group: 'design',
    gentlyOnly: true,
  },
  {
    id: 'schema-definitions',
    title: 'Schema Definitions',
    subtitle: 'Configure extraction and extension schemas',
    icon: ListBulletsIcon,
    group: 'data-model',
  },
  {
    id: 'sync-relay',
    title: 'Sync Relay',
    subtitle: 'Sync agent queries and where each one uploads its CSV extracts',
    icon: ArrowsClockwiseIcon,
    group: 'integrations',
    requiredPermission: {
      action: Action.Read,
      subject: Subject.Integration,
    },
  },
  {
    id: 'task-pages',
    title: 'Task Pages',
    subtitle:
      'Sidebar-mounted task queues for one or more task types, with per-page audiences',
    icon: ListBulletsIcon,
    group: 'data-model',
    requiredPermission: { action: Action.Update, subject: Subject.TaskPage },
  },
  {
    id: 'task-types',
    title: 'Task Types',
    subtitle:
      'Typed-task registry — anchor entities, subtask checklists, outcomes, and custom actions',
    icon: ListBulletsIcon,
    group: 'data-model',
    requiredPermission: { action: Action.Read, subject: Subject.Task },
    featureFlag: 'tasks-enabled',
  },
  {
    id: 'teams',
    title: 'Teams',
    subtitle: 'Group members into teams',
    icon: UsersThreeIcon,
    group: 'team',
    // Group-permission editors (Staff Admins hold UserGroupPermissions) see
    // every team; a bare StaffAdminPermission holder sees only their own group.
    requiredAnyPermission: [
      { action: Action.Read, subject: Subject.UserGroup },
      { action: Action.Read, subject: Subject.UserGroupPermissions },
      { action: Action.Read, subject: Subject.StaffAdminPermission },
    ],
  },
  {
    id: 'template-blocks',
    title: 'Template Blocks',
    subtitle: 'Manage reusable JSON-render fragments for email templates',
    icon: NoteIcon,
    group: 'communication',
  },
  {
    id: 'test-forward-original-graph-message',
    title: 'Test Forward Original Message',
    subtitle:
      'Forward an existing Graph message (order-notification email path)',
    icon: PaperPlaneTiltIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'test-timeout',
    title: 'Test Timeout',
    subtitle: 'Call backend to delay response by specified milliseconds',
    icon: TimerIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'test-upn-write-access',
    title: 'Test UPN Write Access',
    subtitle: 'Send a test email from a UPN to verify write permissions',
    icon: EnvelopeSimpleIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'sentry-test',
    title: 'Trigger Sentry Error',
    subtitle: 'Intentionally throw an error to test Sentry',
    icon: BugIcon,
    group: 'developer',
    gentlyOnly: true,
  },
  {
    id: 'twilio',
    title: 'Twilio',
    subtitle: 'Credentials, sender numbers, and the inbound SMS webhook URL',
    icon: ChatTextIcon,
    group: 'integrations',
    requiredPermission: {
      action: Action.Update,
      subject: Subject.BackgroundAgent,
    },
  },
  {
    id: 'voice-agents',
    title: 'Voice Agents',
    subtitle: 'Configure AI voice agents for outbound calls',
    icon: PhoneIcon,
    group: 'ai',
    gentlyOnly: true,
  },
];

export const DEFAULT_ORG_SECTION_ID: OrganizationSettingsSectionId =
  'custom-fields';

export const SECTION_PATH_PREFIX = '/settings/organization';

export const sectionPath = (id: OrganizationSettingsSectionId) =>
  `${SECTION_PATH_PREFIX}/${id}` as const;

// Legacy slug → current slug map. Used by the legacy $section redirect route so
// bookmarked URLs keep working.
export const LEGACY_SECTION_SLUG_MAP: Record<
  string,
  OrganizationSettingsSectionId
> = {
  'organization-settings': 'general',
  'core-custom-fields': 'custom-fields',
  'core-enums': 'enums',
  'tag-definitions': 'resource-privileges',
  'tag-resources': 'resource-privileges',
  tags: 'resource-privileges',
};

export const SECTION_LABELS = ORGANIZATION_SETTINGS_SECTIONS.reduce<
  Record<string, string>
>((acc, section) => {
  acc[section.id] = section.title;
  return acc;
}, {});

/**
 * Every permission any section is gated on, deduplicated — what a viewer has
 * to be checked against to know which sections they may open. Derived from
 * the section list so a new gate can never be forgotten by a caller.
 */
/**
 * Holding this opens the staff-only (`gentlyOnly`) sections, like being Gently
 * staff does. Update-only on purpose: viewing them is staff-only too, because
 * reading org config is `OrganizationConfig`.
 */
export const STAFF_SECTIONS_PERMISSION = {
  action: Action.Update,
  subject: Subject.OrganizationSettings,
} as const;

export const ORGANIZATION_SETTINGS_PERMISSION_CHECKS: ReadonlyArray<{
  action: Action;
  subject: Subject;
}> = (() => {
  const byKey = new Map<string, { action: Action; subject: Subject }>();
  // Opens every `gentlyOnly` section alongside Gently staff (`isAdmin`).
  byKey.set(
    `${STAFF_SECTIONS_PERMISSION.action}:${STAFF_SECTIONS_PERMISSION.subject}`,
    STAFF_SECTIONS_PERMISSION,
  );
  for (const section of ORGANIZATION_SETTINGS_SECTIONS) {
    for (const permission of [
      ...(section.requiredPermission ? [section.requiredPermission] : []),
      ...(section.requiredAnyPermission ?? []),
    ]) {
      byKey.set(`${permission.action}:${permission.subject}`, permission);
    }
  }
  return [...byKey.values()];
})();

export const getOrganizationSettingsSections = ({
  gently,
  canManageStaffSections = false,
  isModuleEnabled,
  hasPermission,
  isFeatureFlagEnabled,
  famousIntegrationEnabled,
}: {
  gently: boolean;
  /** Holds `Update OrganizationSettings`: opens `gentlyOnly` sections only. */
  canManageStaffSections?: boolean;
  isModuleEnabled: (module: ProductModule) => boolean;
  hasPermission: (action: Action, subject: Subject) => boolean;
  isFeatureFlagEnabled: (flag: FeatureFlagKey) => boolean;
  famousIntegrationEnabled: boolean;
}) =>
  // fallow-ignore-next-line complexity
  ORGANIZATION_SETTINGS_SECTIONS.filter((section) => {
    if (section.gentlyOnly && !gently && !canManageStaffSections) {
      return false;
    }
    if (section.requiredModule && !isModuleEnabled(section.requiredModule)) {
      return false;
    }
    if (section.requiresFamousIntegration && !famousIntegrationEnabled) {
      return false;
    }
    if (
      section.requiredPermission &&
      !gently &&
      !hasPermission(
        section.requiredPermission.action,
        section.requiredPermission.subject,
      )
    ) {
      return false;
    }
    if (
      section.requiredAnyPermission &&
      !gently &&
      !section.requiredAnyPermission.some(({ action, subject }) =>
        hasPermission(action, subject),
      )
    ) {
      return false;
    }
    return !isSectionHiddenByFlags(section, isFeatureFlagEnabled);
  });

/**
 * Whether a section's URL is closed to this viewer by its synchronous gates:
 * staff-only, required module, or flags. The route guard checks the
 * permission gate separately, since that one is async.
 */
/** The Agent Harness review pages Knowledge replaces while memory learning is on. */
const KNOWLEDGE_REVIEW_COPY = {
  'harness-proposals': 'knowledge-proposals',
  'harness-memories': 'knowledge-memories',
  'harness-corrections': 'knowledge-corrections',
} as const satisfies Partial<
  Record<OrganizationSettingsSectionId, OrganizationSettingsSectionId>
>;

type HarnessReviewSectionId = keyof typeof KNOWLEDGE_REVIEW_COPY;

/**
 * The review page a link should open: the Knowledge copy while
 * `memory-learning-enabled` hides the Agent Harness one, else the original.
 */
export function learningReviewSection(
  id: HarnessReviewSectionId,
  memoryLearningEnabled: boolean,
): OrganizationSettingsSectionId {
  return memoryLearningEnabled ? KNOWLEDGE_REVIEW_COPY[id] : id;
}

/** {@link learningReviewSection} for a full settings path, e.g. a graph node's link. */
export function learningReviewHref(
  href: string | null,
  memoryLearningEnabled: boolean,
): string | null {
  if (!href || !memoryLearningEnabled) return href;
  const id = href.slice(href.lastIndexOf('/') + 1);
  return isHarnessReviewSection(id) && href === sectionPath(id)
    ? sectionPath(KNOWLEDGE_REVIEW_COPY[id])
    : href;
}

function isHarnessReviewSection(id: string): id is HarnessReviewSectionId {
  return Object.hasOwn(KNOWLEDGE_REVIEW_COPY, id);
}

export function isSectionRouteClosed(
  section: Pick<
    OrganizationSettingsSection,
    'gentlyOnly' | 'requiredModule' | 'featureFlag' | 'hiddenWhenFeatureFlag'
  >,
  viewer: {
    isGentlyStaff: boolean;
    isModuleEnabled: (module: ProductModule) => boolean;
    isFeatureFlagEnabled: (flag: FeatureFlagKey) => boolean;
  },
): boolean {
  if (section.gentlyOnly && !viewer.isGentlyStaff) return true;
  if (section.requiredModule && !viewer.isModuleEnabled(section.requiredModule))
    return true;
  return isSectionHiddenByFlags(section, viewer.isFeatureFlagEnabled);
}

/**
 * Whether a section's flags hide it: its `featureFlag` is off, or its
 * `hiddenWhenFeatureFlag` is on. The menu, Quick Find and the section's URL
 * all use this, so a hidden section can't be reached another way.
 */
export function isSectionHiddenByFlags(
  section: Pick<
    OrganizationSettingsSection,
    'featureFlag' | 'hiddenWhenFeatureFlag'
  >,
  isFeatureFlagEnabled: (flag: FeatureFlagKey) => boolean,
): boolean {
  if (section.featureFlag && !isFeatureFlagEnabled(section.featureFlag)) {
    return true;
  }
  return (
    !!section.hiddenWhenFeatureFlag &&
    isFeatureFlagEnabled(section.hiddenWhenFeatureFlag)
  );
}
